import { fetchItemSummariesIfNeeded } from '@/redux/modules/item/summary/itemSummary.actions';
import { FormattedMessage, FormattedNumber } from '@liveauctioneers/hammer-ui-core/intl';
import { getLimitedSortedRecentItemIdsFromState } from '@/redux/modules/recentItem';
import { trackRecentItemClick } from '@/redux/modules/analytics';
import { useAppDispatch, useAppSelector } from '@/redux/hooks';
import { useCallback } from 'react';
import EnsureLoggedInContainer from '@/components/EnsureLoggedIn/EnsureLoggedInContainer';
import FluidItemGrid from '@/components/GridItemCard/FluidItemGrid/FluidItemGrid';
import PageTitle from '@/components/PageTitle/PageTitle';
import RecentItemsPageHeaderMeta from './RecentItemsPageHeaderMeta';
import styled from 'styled-components';
import TimedOrCategorySearchCard from '@/components/GridItemCard/Card/MultiCardOptions/TimedOrCategorySearchCard/TimedOrCategorySearchCard';
import useDeepCompareEffect from 'use-deep-compare-effect';

const RecentItemsPage = () => {
    const dispatch = useAppDispatch();
    const itemIds = useAppSelector((state) => getLimitedSortedRecentItemIdsFromState(state, 72));

    const track = useCallback(
        (itemId: number) => {
            dispatch(trackRecentItemClick(itemId));
        },
        [dispatch]
    );

    useDeepCompareEffect(() => {
        dispatch(
            fetchItemSummariesIfNeeded({
                identifier: 'RECENT-ITEMS-PAGE',
                itemIds,
            })
        );
    }, [dispatch, itemIds]);

    return (
        <EnsureLoggedInContainer>
            <StyledPageContainer id="recentItems">
                <RecentItemsPageHeaderMeta />
                <StyledTitleCount>
                    <PageTitle title={<FormattedMessage id="recently_viewed" />} />
                    <StyledResultsCount>
                        <FormattedMessage
                            id="search_item_count_items"
                            key="recent-items-page-search-item-count-items"
                            values={{
                                count: (
                                    <FormattedNumber
                                        key="recent-items-page-results-count"
                                        style="decimal"
                                        value={itemIds.length}
                                    />
                                ),
                            }}
                        />
                    </StyledResultsCount>
                </StyledTitleCount>
                <FluidItemGrid
                    itemCardOption={TimedOrCategorySearchCard}
                    itemIds={itemIds}
                    trackClick={track}
                />
            </StyledPageContainer>
        </EnsureLoggedInContainer>
    );
};

export default RecentItemsPage;

const StyledPageContainer = styled.div`
    flex: 1 1;
    position: relative;
    min-height: 400px;
    padding: 0 20px 50px;
    max-width: ${({ theme }) => theme.breakpoints.pageWidth};
    margin: 0 auto;
`;

const StyledTitleCount = styled.div`
    @media (max-width: 416px) {
        flex-direction: column;
        align-items: center;
        text-align: center;
    }

    display: flex;
    flex-flow: row wrap;
    align-items: baseline;
    margin-bottom: 30px;
    font-size: 18px;
`;

const StyledResultsCount = styled.span`
    color: ${({ theme }) => theme.colors.grey300};
    font-size: 14px;
    letter-spacing: 0.5px;
    padding-right: 6px;
    line-height: 25px;
    margin-left: 8px;
`;
